// frontend/src/components/admin/PostArticle.js
// Admin-only: Post a pinned article that appears top-right on the homepage
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './PostArticle.css';

const CATEGORIES = ['crypto', 'ai', 'politics'];

const PostArticle = () => {
  const [form, setForm] = useState({
    title: '',
    content: '',
    category: 'crypto',
    image_url: '',
    source_name: 'FocusView Editorial',
  });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [pinnedArticle, setPinnedArticle] = useState(null);
  const [loadingPinned, setLoadingPinned] = useState(true);

  useEffect(() => {
    fetchPinnedArticle();
  }, []);

  const fetchPinnedArticle = async () => {
    try {
      const res = await api.get('/admin/pinned-article');
      setPinnedArticle(res.data);
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error('Error fetching pinned article:', err);
      }
      setPinnedArticle(null);
    } finally {
      setLoadingPinned(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      setMessage({ type: 'error', text: 'Title and content are required.' });
      return;
    }

    setSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const res = await api.post('/admin/post-article', form);
      setMessage({ type: 'success', text: '✅ Article posted and pinned to homepage!' });
      setPinnedArticle(res.data);
      setForm({
        title: '',
        content: '',
        category: 'crypto',
        image_url: '',
        source_name: 'FocusView Editorial',
      });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to post article. Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnpin = async () => {
    if (!window.confirm('Remove the currently pinned article from the homepage?')) return;
    try {
      await api.delete('/admin/pinned-article');
      setPinnedArticle(null);
      setMessage({ type: 'success', text: '✅ Pinned article removed.' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to remove pinned article.' });
    }
  };

  return (
    <div className="post-article-container">
      <div className="post-article-header">
        <h2>📌 Post Admin Article</h2>
        <p className="post-article-desc">
          This article will be <strong>pinned to the top-right</strong> of the homepage and stays
          visible on every page refresh. Only one pinned article at a time.
        </p>
      </div>

      {/* Currently Pinned */}
      <div className="pinned-section">
        <h3>Currently Pinned</h3>
        {loadingPinned ? (
          <p className="pinned-loading">Loading...</p>
        ) : pinnedArticle ? (
          <div className="pinned-preview">
            <div className="pinned-preview-content">
              <span className="pinned-badge">📌 PINNED</span>
              <span className="pinned-category">{pinnedArticle.category?.toUpperCase()}</span>
              <h4>{pinnedArticle.title}</h4>
              <p>{pinnedArticle.ai_summary || pinnedArticle.summary || pinnedArticle.content?.substring(0, 120) + '...'}</p>
              <small>Posted: {new Date(pinnedArticle.published_at).toLocaleString()}</small>
            </div>
            <button className="unpin-btn" onClick={handleUnpin}>
              🗑️ Remove Pin
            </button>
          </div>
        ) : (
          <p className="no-pinned">No article currently pinned. Post one below.</p>
        )}
      </div>

      {/* Post Form */}
      <div className="post-form-section">
        <h3>Post New Article</h3>

        {message.text && (
          <div className={`post-message ${message.type}`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="post-article-form">
          <div className="form-row">
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="Enter article headline..."
              maxLength={250}
              required
              disabled={submitting}
            />
            <span className="char-count">{form.title.length}/250</span>
          </div>

          <div className="form-row two-col">
            <div>
              <label htmlFor="category">Category</label>
              <select
                id="category"
                name="category"
                value={form.category}
                onChange={handleChange}
                disabled={submitting}
              >
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="source_name">Source Name</label>
              <input
                type="text"
                id="source_name"
                name="source_name"
                value={form.source_name}
                onChange={handleChange}
                placeholder="e.g. FocusView Editorial"
                disabled={submitting}
              />
            </div>
          </div>

          <div className="form-row">
            <label htmlFor="image_url">Image URL <span className="optional">(optional)</span></label>
            <input
              type="url"
              id="image_url"
              name="image_url"
              value={form.image_url}
              onChange={handleChange}
              placeholder="https://example.com/image.jpg"
              disabled={submitting}
            />
            {form.image_url && (
              <img src={form.image_url} alt="preview" className="image-preview" onError={e => e.target.style.display='none'} />
            )}
          </div>

          <div className="form-row">
            <label htmlFor="content">Article Content *</label>
            <textarea
              id="content"
              name="content"
              value={form.content}
              onChange={handleChange}
              placeholder="Write the article content here. It will be AI-rephrased before publishing..."
              rows={10}
              maxLength={10000}
              required
              disabled={submitting}
            />
            <span className="char-count">{form.content.length}/10,000</span>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="post-btn"
              disabled={submitting || !form.title.trim() || !form.content.trim()}
            >
              {submitting ? '⏳ Publishing...' : '📌 Post & Pin to Homepage'}
            </button>
            <button
              type="button"
              className="clear-btn"
              onClick={() => setForm({ title: '', content: '', category: 'crypto', image_url: '', source_name: 'FocusView Editorial' })}
              disabled={submitting}
            >
              Clear
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PostArticle;
